// Public landing page for taskpogo.com. Combines marketing copy with live
// demos of real product controls (TaskCard, UpcomingTasks,
// HourMeter, StatusBadge) so the landing page stays in sync with
// production UI automatically — when those controls change, this page
// reflects the change without a copy refresh.
//
// Demo controls are fed by `src/demo/demoData.js` (production-safe; no
// `test-utils` import) and wrapped by `<DemoBoundary>`; they're rendered
// with no-op handlers so internal clicks never call useNavigate.

import Button from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';

import {
  UpcomingTasks,
  HourMeter,
  StatusBadge,
  DataCard,
  CardHeader,
} from '../components';
import { displayDate } from '../fleet-shared/Common';
import { TaskCard } from './task/TaskCard';
import {
  nameOverdue,
  nameSoon,
  nameAll,
} from '../fleet-shared/TaskBuckets.mjs';
import { urlForAllItems, urlForAllTrips, urlForSalish } from '../fleet-shared/Urls';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '../fleet-shared/Brand.mjs';
import { itemTypeIcon } from '../fleet-shared/ItemTypes.mjs';
import TripGroupsTable from './trip/TripGroupsTable';

import {
  demoBuckets,
  demoFeaturedTask,
  demoCounters,
  demoLogbookEntries,
  demoTripGroups,
  demoCaptainsEntries,
} from '../demo/demoData';
import { DemoBoundary } from '../demo/DemoBoundary';

import './Home.css';

const noop = () => {};
const noopAsync = async () => {};

// ---- Hero -----------------------------------------------------------------

// Stand-in for the welcome bar while App is still resolving the session.
// Reuses .home-hero__welcome so the chrome and flex layout are identical and
// the real bar lands in the same place with no shift. The blocks are decorative
// (the wait is announced once, here); .visually-hidden is Bootstrap's.
const WelcomeSkeleton = () => (
  <div className="home-hero__welcome home-hero__welcome--loading" role="status">
    <span className="visually-hidden">Checking your account…</span>
    <span
      className="tp-skeleton home-hero__welcome-skeleton-text"
      aria-hidden="true"
    />
    <span
      className="tp-skeleton home-hero__welcome-skeleton-cta"
      aria-hidden="true"
    />
  </div>
);

// Three states, not two: while `isInitializing` we don't yet know which hero to
// show, and guessing means a signed-in visitor watches "Get started — it's
// free" flip to their own name. Default it to false so the page stays purely
// prop-driven for callers that don't track auth (tests, storybook-ish uses).
const Hero = ({ user, isInitializing = false, onPrimaryCta }) => {
  const signedIn = user !== null && user !== undefined;

  return (
    <header className="home-hero">
      <div className="container">
        <div className="home-hero__inner">
          {isInitializing && <WelcomeSkeleton />}

          {!isInitializing && signedIn && (
            // The signed-in "Welcome back" eyebrow doubles as the primary
            // CTA bar: visitors who already have an account get a direct
            // jump to their trips right next to the greeting, so they
            // don't have to scan past the marketing pitch to find it.
            <div className="home-hero__welcome">
              <span className="home-hero__welcome-text">
                Welcome back{user.email ? `, ${user.email}` : ''}.
              </span>
              <Button
                variant="primary"
                onClick={onPrimaryCta}
                className="home-cta-primary home-cta-primary--compact"
              >
                Open your trips
              </Button>
            </div>
          )}

          <h1 className="home-hero__headline">
            Plan the trip. Watch the weather.<br className="d-none d-md-inline" />{' '}
            Keep the log.
          </h1>
          {/*
            The tagline lives in Brand.mjs and closes on "an airplane, a house,
            or a car" on purpose: non-boat use cases are first-class, so they
            belong in the hero rather than only in a section far down the page.
          */}
          <p className="home-hero__sub">{PRODUCT_TAGLINE}</p>

          <div className="home-hero__actions">
            {!isInitializing && !signedIn && (
              <Button
                variant="primary"
                size="lg"
                onClick={onPrimaryCta}
                className="home-cta-primary"
              >
                Get started — it’s free
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// ---- Section shell --------------------------------------------------------

// Stacked layout: copy block on top, full-width demo below. Earlier two-
// column variants were dropped because the right-hand demo column was
// too narrow to render UpcomingTasks' three bucket columns without
// truncating headings ("Over...", "30 days..." -- the Soon bucket's heading
// names the item's due-soon window).
const Section = ({ id, eyebrow, title, blurb, bullets, demo, variant = 'default' }) => (
  <section id={id} className={`home-section home-section--${variant}`}>
    <div className="container">
      <div className="home-section__copy">
        {eyebrow && <div className="home-section__eyebrow">{eyebrow}</div>}
        <h2 className="home-section__title">{title}</h2>
        {blurb && <p className="home-section__blurb">{blurb}</p>}
        {bullets && (
          <ul className="home-section__bullets">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
      </div>

      <DemoBoundary
        className="home-section__demo"
        ariaLabel={`${title} demo`}
      >
        {demo}
      </DemoBoundary>
    </div>
  </section>
);

// ---- Status badge strip ---------------------------------------------------

const BadgeStrip = () => (
  <div className="home-badges">
    <div className="home-badges__row">
      <StatusBadge status={nameOverdue} />
      <span>Past due — needs attention now.</span>
    </div>
    <div className="home-badges__row">
      <StatusBadge status={nameSoon} />
      <span>Coming up in the next 30 days (or 50 counter units) — each item can set its own window.</span>
    </div>
    <div className="home-badges__row">
      <StatusBadge status={nameAll} />
      <span>Tracked, but not yet due. You’ll know in plenty of time.</span>
    </div>
  </div>
);

// ---- Items example --------------------------------------------------------

// A deliberately mixed fleet. The boat leads because the product does, but the
// other three are the point of this section: every feature above works for
// them, and the icons say so before the copy does.
const ITEMS_PREVIEW = [
  { name: 'Enterprise', type: 'boat', detail: 'Grand Banks 42 · 3 tasks · 1 overdue' },
  { name: 'N7127T', type: 'airplane', detail: "Cessna 172 · Pilot's Logbook · 2 tasks" },
  { name: 'Vacation Home', type: 'home', detail: 'Smoke detectors due in 18 days' },
  { name: '2019 Subaru Outback', type: 'vehicle', detail: '48,210 mi · 2 tasks' },
];

const ItemsPreview = () => (
  <DataCard
    header={<CardHeader text="Your items" />}
    body={
      <div className="home-items">
        {ITEMS_PREVIEW.map((it) => (
          <div className="home-items__row" key={it.name}>
            <div className="home-items__icon" aria-hidden="true">
              {/*
                Same registry the app draws from, so this preview cannot drift
                from what a real items list shows. A row with no `type` falls
                through itemTypeById to the neutral fa-cube -- which is exactly
                what every item, typed or not, renders today.
              */}
              <i className={itemTypeIcon(it.type)} />
            </div>
            <div className="home-items__text">
              <div className="home-items__name entered-data">{it.name}</div>
              <div className="home-items__detail">{it.detail}</div>
            </div>
            <i
              className="fa-solid fa-chevron-right home-items__chev"
              aria-hidden="true"
            />
          </div>
        ))}
      </div>
    }
  />
);

// ---- Trips demo -----------------------------------------------------------

// The real TripGroupsTable, fed by demo legs run through the real groupTrips().
// It takes no router hook of its own -- `onDetails` is the only way out of it,
// so a no-op keeps a landing visitor off the auth-gated app.
//
// No placeIndex: the gazetteer is a signed-in read, and every demo endpoint is
// already spelled the way it should display.
const TripsDemo = () => (
  <DataCard
    header={<CardHeader text="Trips" />}
    body={<TripGroupsTable groups={demoTripGroups()} onDetails={noop} />}
  />
);

// ---- Weather demo ---------------------------------------------------------

// Deliberately NOT a live WeatherDetailsCard fed by a canned fixture. Two
// reasons: the real card is a pure function of "did any features arrive", so a
// fixture would be a large hand-maintained copy of upstream shapes that nobody
// would notice going stale; and /salish is a real, public, un-gated page
// showing the same data live. Pointing at it is both less code and a better
// demo than anything canned.
const WeatherDemo = () => (
  <div className="home-weather-card">
    <div className="home-weather-card__grid">
      <div className="home-weather-card__stat">
        <div className="home-weather-card__label">Wind</div>
        <div className="home-weather-card__value entered-data">SW 15 kt</div>
      </div>
      <div className="home-weather-card__stat">
        <div className="home-weather-card__label">Seas</div>
        <div className="home-weather-card__value entered-data">2 ft</div>
      </div>
      <div className="home-weather-card__stat">
        <div className="home-weather-card__label">High water</div>
        <div className="home-weather-card__value entered-data">10:42 · 8.1 ft</div>
      </div>
      <div className="home-weather-card__stat">
        <div className="home-weather-card__label">Slack</div>
        <div className="home-weather-card__value entered-data">09:18</div>
      </div>
    </div>
    <p className="home-weather-card__note">
      Forecasts from the US National Weather Service and Environment and Climate
      Change Canada; observations from NDBC buoys and airport stations; tides and
      currents from NOAA and the Canadian Hydrographic Service. For planning only
      — not for navigation.
    </p>
    <a className="home-weather-card__link" href={urlForSalish()}>
      See live Salish Sea conditions →
    </a>
  </div>
);

// ---- Captain's Log demo ---------------------------------------------------

// A read-only mirror of the Captain's Log columns, for the same reason
// LogbookDemo is one: the real row component navigates on click.
const CaptainsLogDemo = () => (
  <DataCard
    header={<CardHeader text="Captain's Log" />}
    body={
      <div className="home-captains">
        {demoCaptainsEntries().map((entry) => (
          <div className="home-captains__row" key={entry.id}>
            <div className="home-captains__date entered-data">
              {displayDate(entry.date)}
            </div>
            <div className="home-captains__route entered-data">
              {entry.from} → {entry.to}
            </div>
            <div className="home-captains__trip">{entry.tripName}</div>
            <div className="home-captains__hours entered-data">
              {entry.hours.toFixed(1)} h
            </div>
          </div>
        ))}
      </div>
    }
  />
);

// ---- Logbook demo ---------------------------------------------------------

// Inline logbook rows. The real LogbookEntrySummary unconditionally calls
// useNavigate() in its "See Details" handler, which would yank a landing
// visitor into the auth-gated app. The demo intentionally renders a
// read-only mirror of the same row layout instead.
const LogbookDemo = () => {
  const entries = demoLogbookEntries();
  return (
    <DataCard
      header={<CardHeader text="Logbook" />}
      body={
        <div className="home-logbook">
          {entries.map((entry) => (
            <div className="home-logbook__row" key={entry.id}>
              <div className="home-logbook__date entered-data">
                {displayDate(entry.date)}
              </div>
              <div className="home-logbook__name entered-data">
                {entry.name}
              </div>
              <div className="home-logbook__cta" aria-hidden="true">
                Details
              </div>
            </div>
          ))}
        </div>
      }
    />
  );
};

// ---- Email subscription mock ----------------------------------------------

const EmailDemo = () => (
  <div className="home-email-card">
    <div className="home-email-card__head">
      <i
        className="fa-regular fa-envelope home-email-card__icon"
        aria-hidden="true"
      />
      <div>
        <div className="home-email-card__from">{PRODUCT_NAME}</div>
        <div className="home-email-card__subject">
          Desolation Sound — Fri: SW 15 kt, small craft advisory
        </div>
      </div>
    </div>
    <div className="home-email-card__body">
      <div className="home-email-card__line">
        <span className="home-email-card__leg">Fri · Nanaimo → Squirrel Cove</span>
      </div>
      <div className="home-email-card__line">
        <span>Strait of Georgia: SW 15 to 20 kt, seas 2 to 3 ft.</span>
      </div>
      <div className="home-email-card__line">
        <span>Slack at Dodd Narrows 09:18. High water Nanaimo 10:42, 8.1 ft.</span>
      </div>
      <div className="home-email-card__cta">
        <span className="home-email-card__link">View in {PRODUCT_NAME} →</span>
      </div>
    </div>
  </div>
);

// The maintenance digest, which is a different email with a different job:
// what's overdue and what's due soon on an item, on a schedule you pick.
const StatusEmailDemo = () => (
  <div className="home-email-card">
    <div className="home-email-card__head">
      <i
        className="fa-regular fa-envelope home-email-card__icon"
        aria-hidden="true"
      />
      <div>
        <div className="home-email-card__from">{PRODUCT_NAME}</div>
        <div className="home-email-card__subject">
          Enterprise: 1 overdue, 1 due soon
        </div>
      </div>
    </div>
    <div className="home-email-card__body">
      <div className="home-email-card__line">
        <StatusBadge status={nameOverdue} />
        <span>Oil change — was due at 1900 engine hours.</span>
      </div>
      <div className="home-email-card__line">
        <StatusBadge status={nameSoon} />
        <span>Replace smoke-detector batteries — due in 18 days.</span>
      </div>
      <div className="home-email-card__cta">
        <span className="home-email-card__link">View in {PRODUCT_NAME} →</span>
      </div>
    </div>
  </div>
);

// ---- Page -----------------------------------------------------------------

const Home = ({ user, isInitializing = false }) => {
  const navigate = useNavigate();
  const signedIn = user !== null && user !== undefined;

  function goToItems() {
    navigate(urlForAllItems());
  }

  // Signed-in visitors land on Trips -- the page order above already says the
  // product leads with the trip, and the nav does too. Signed-out visitors
  // keep routing through /me/item: that URL is the Authenticator gate, i.e.
  // the signup surface, and changing it is a different decision.
  function goToTrips() {
    navigate(urlForAllTrips());
  }
  const primaryCta = signedIn ? goToTrips : goToItems;

  return (
    <main className="home">
      <Hero
        user={user}
        isInitializing={isInitializing}
        onPrimaryCta={primaryCta}
      />

      {/*
        Section order is the pitch, and it is deliberate: plan the trip, see
        the weather for it, log the passage, then keep the boat running. The
        maintenance sections come after the trip ones because that is the
        change -- they used to be the whole page.

        The last section is the non-boat commitment. It is a section rather
        than a footnote because those uses are first-class: everything above
        works for them unchanged.
      */}
      <Section
        id="how-it-works"
        eyebrow="Trips"
        title="Plan the trip, leg by leg"
        blurb="A trip is a series of legs — where you're going, when, and how long you're staying. TaskPogo groups them into one trip with its own page, route, and calendar, so a two-week cruise reads as a trip rather than fourteen rows."
        bullets={[
          'Drop waypoints on the map and the distance is measured for you.',
          'Places you name are remembered, so the next trip starts from them.',
          'A month calendar shows where you are each day.',
        ]}
        demo={<TripsDemo />}
        variant="trips"
      />

      <Section
        eyebrow="Weather"
        title="The forecast for where you're actually going"
        blurb="Marine area forecasts and warnings for the waters your route crosses, conditions from the nearest buoys and stations, and the tides and currents at the passes you have to time — all on the trip's own map."
        bullets={[
          'Marine zones and warnings from the NWS and Environment Canada.',
          'Tides and tidal currents from NOAA and the Canadian Hydrographic Service.',
          'Timed to the day you arrive, not to today.',
        ]}
        demo={<WeatherDemo />}
        variant="weather"
      />

      <Section
        eyebrow="Captain's Log"
        title="Every passage, in one place"
        blurb="Log a passage against the trip it belongs to and the Captain's Log keeps the record — where you went, when, and how long it took. An airplane gets a Pilot's Logbook on the same terms."
        bullets={[
          'Entries file themselves under the trip they belong to.',
          'One trip’s entries share a page and a map.',
          'Hours and trip counts totalled by year.',
        ]}
        demo={<CaptainsLogDemo />}
        variant="captains"
      />

      <Section
        eyebrow="Maintenance"
        title="Due by date, or by engine hours"
        blurb="Some work comes due on a date — bottom paint, registration, a safety inspection. The rest comes due on use: oil at every 100 hours, filters at every 5,000 miles. TaskPogo tracks both, and tells you which is overdue."
        bullets={[
          'Counters for engine hours, nautical miles, or cycles.',
          'Tasks repeat on an interval (every 100 hours, every 365 days).',
          'Overdue means overdue — by either clock or counter.',
        ]}
        demo={
          <>
            <UpcomingTasks
              buckets={demoBuckets()}
              onClickTask={noop}
              setTaskBucket={noop}
              setShowTaskBucket={noop}
              addTask={noopAsync}
            />
            <HourMeter counterItems={demoCounters()} />
            <div className="home-featured-task">
              <TaskCard task={demoFeaturedTask()} onTaskClicked={noop} />
            </div>
          </>
        }
        variant="dashboard"
      />

      <Section
        eyebrow="Maintenance log"
        title="What you did, and when you did it"
        blurb="Completed work lands in the maintenance log with the date and the counter reading at the time. Look up when the zincs were last changed without digging through a paper notebook in the lazarette."
        bullets={[
          'Searchable history of every completed task.',
          'The counter reading at the time of completion.',
          'Great for resale, surveys, or just remembering when.',
        ]}
        demo={<LogbookDemo />}
        variant="logbook"
      />

      <Section
        eyebrow="Email"
        title="A briefing before you go"
        blurb="Schedule a daily weather email for a trip and it arrives before you leave the dock — marine forecasts and warnings for the waters you're crossing, conditions now, and the tides and currents for each leg. Items can send a maintenance digest on the same terms."
        bullets={[
          'A daily trip briefing, at an hour and time zone you pick.',
          'Per-item status digests for co-owners or a shop manager.',
          'Unsubscribe with one click — no account needed for the recipient.',
        ]}
        demo={
          <>
            <EmailDemo />
            <BadgeStrip />
            <StatusEmailDemo />
          </>
        }
        variant="email"
      />

      <Section
        eyebrow="Not just boats"
        title="Anything else you look after"
        blurb="An Item is whatever you're maintaining. TaskPogo started as a maintenance tracker and still is one — an airplane, a house, a vacation home, a car, or a project gets the same tasks, counters, logbook and email digests as the boat does."
        bullets={[
          'An airplane with a Pilot’s Logbook and cycles on the airframe.',
          'A house with smoke-detector batteries due on a date.',
          'A car with oil changes due on the odometer.',
        ]}
        demo={<ItemsPreview />}
        variant="items"
      />
      <section className="home-closing">
        <div className="container text-center">
          <h2 className="home-closing__title">Ready for the season?</h2>
          <p className="home-closing__sub">
            {PRODUCT_NAME} is free to try. Add your boat — or whatever else you
            look after — in under a minute.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={primaryCta}
            className="home-cta-primary"
          >
            {signedIn ? 'Open your trips' : 'Create account or log in'}
          </Button>
        </div>
      </section>
    </main>
  );
};

export default Home;
